// src/tools/practice-tracker/api.ts
import { supabase } from "../../app/supabaseClient"

export type LessonTool = {
  id: string
  lesson_note_id: string
  student_id: string
  tool_config: {
    slug: string
    title?: string
    icon_url?: string
    goal_sessions?: number
    resources_path?: string
    play_celebration_sound?: boolean
    celebration_audio_url?: string
  }
}

export async function getLessonTool(lessonToolId: string): Promise<LessonTool> {
  const { data, error } = await supabase
    .from('lesson_tools')
    .select('id, lesson_note_id, student_id, tool_config')
    .eq('id', lessonToolId)
    .single()
  if (error) throw error
  
  // Ensure tool_config has default structure
  const lessonTool = data as any
  if (!lessonTool.tool_config) {
    lessonTool.tool_config = { slug: 'practice-tracker' }
  }
  
  return lessonTool as LessonTool
}

  export async function getOrCreatePracticeTracker(lessonTool: LessonTool, _currentAuthUserId: string) {
    // 1) Try to find existing
    const { data: existing } = await supabase
      .from('practice_trackers')
      .select('*')
      .eq('lesson_tool_id', lessonTool.id)
      .maybeSingle();
    if (existing) return existing;

    // 2) Fetch teacher_id from the note (users.id) to satisfy FK on created_by
    const { data: noteRow, error: noteErr } = await supabase
      .from('lesson_notes')
      .select('teacher_id')
      .eq('id', lessonTool.lesson_note_id)
      .single();
    if (noteErr || !noteRow?.teacher_id) {
      throw noteErr || new Error('Could not resolve teacher_id for lesson note');
    }

    const goal = Math.max(1, Number(lessonTool.tool_config?.goal_sessions) || 5);

    // 3) Create (or race-proof upsert) using teacher_id for created_by
    const { data, error } = await supabase
      .from('practice_trackers')
      .upsert(
        {
          lesson_note_id: lessonTool.lesson_note_id,
          lesson_tool_id: lessonTool.id,
          student_id: lessonTool.student_id,
          goal_sessions: goal,
          created_by: noteRow.teacher_id, // <-- users.id, satisfies FK
        },
        { onConflict: 'lesson_tool_id' }
      )
      .select()
      .single();

    if (error) throw error;
    return data;
  }

export async function getPracticeLogsCount(trackerId: string): Promise<number> {
  const { count, error } = await supabase
    .from('practice_logs')
    .select('id', { count: 'exact', head: true })
    .eq('practice_tracker_id', trackerId)
  if (error) throw error
  return count ?? 0
}

export async function insertPracticeLog(trackerId: string, studentId?: string) {
  let sid = studentId;

  if (!sid) {
    const { data: tracker, error: tErr } = await supabase
      .from('practice_trackers')
      .select('student_id')
      .eq('id', trackerId)
      .single();
    if (tErr) throw tErr;
    sid = tracker.student_id;
  }

  const { data, error } = await supabase
    .from('practice_logs')
    .insert({
      practice_tracker_id: trackerId,
      student_id: sid
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateGoal(trackerId: string, goal: number) {
  const safe = Math.max(1, Math.min(100, Number(goal) || 1));
  const { data, error } = await supabase
    .from('practice_trackers')
    .update({ goal_sessions: safe })
    .eq('id', trackerId)
    .select()
    .single();
  if (error) throw error;
  return data;
}